import { shortUserFromDto } from '@src/features/users/lib/user';

import type {
  ClientCard,
  ClientCardDto,
  ClientCardGroup,
  ClientGroupAutocompleteDto,
  ClientMatch,
  ClientMatchDto,
  CreateClientContact,
  CreateClientPayload,
} from '../types';
import { clientAddressFromDto } from './address';

/** A group as the group autocomplete and the client cards give it. */
export function clientGroupOptionFromDto({
  legal_name,
  ...group
}: ClientGroupAutocompleteDto): ClientCardGroup {
  return { ...group, legalName: legal_name };
}

export function clientCardFromDto(card: ClientCardDto): ClientCard {
  return {
    id: card.id,
    name: card.name,
    legalName: card.legal_name,
    businessSegment: card.business_segment,
    active: card.active,
    sector: card.sector,
    seat: card.seat,
    group: clientGroupOptionFromDto(card.group),
    canView: card.can_view,
  };
}

export function clientMatchFromDto(match: ClientMatchDto): ClientMatch {
  const { prefill } = match;
  return {
    ...clientCardFromDto(match),
    prefill: {
      sectorId: prefill.clie_sect,
      relationshipStatusId: prefill.clie_clrs,
      manager: shortUserFromDto(prefill.manager),
      coordinator: shortUserFromDto(prefill.coordinator),
      registryNumber: prefill.clie_company_registry_number,
      vatNumber: prefill.clie_vat_number,
      revenuePotential: prefill.clie_estimated_revenue_potential,
      website: prefill.clie_website,
      phone: prefill.clie_phone,
      notes: prefill.clie_notes,
      address: prefill.address
        ? {
            ...clientAddressFromDto(prefill.address),
            email: prefill.address.email,
          }
        : null,
    },
  };
}

function contactToDto(contact: CreateClientContact) {
  return {
    existing_contact: contact.existingContactId,
    previous_end: contact.previousEnd,
    title: contact.titleId,
    academic_title: contact.academicTitle,
    family_name: contact.familyName,
    first_name: contact.firstName,
    chosen_name: contact.chosenName,
    language: contact.languageId,
    user_contact: contact.userContactId,
    user_contact2: contact.userContact2Id,
    function_title: contact.functionTitle,
    role: contact.role,
    email: contact.email,
    mobile: contact.mobile,
    landline: contact.landline,
    start: contact.start,
  };
}

/**
 * The body of "create client": the sections as a JSON `payload`, the logo
 * of a new group as the file `file__group__clgr_icon`, as the section
 * commits send them.
 */
export function createClientFormData(payload: CreateClientPayload): FormData {
  const formData = new FormData();
  const { group, client, address } = payload;
  let groupSection: Record<string, unknown>;
  if ('id' in group) {
    groupSection = { id: group.id };
  } else {
    groupSection = {
      clgr_name: group.name,
      clgr_sect: group.sectorId,
      clgr_clrs: group.relationshipStatusId,
      clgr_manager: group.managerId,
      clgr_coordinator: group.coordinatorId,
      clgr_estimated_revenue_potential: group.revenuePotential,
      clgr_website: group.website,
      clgr_notes: group.notes,
    };
    if (group.logo) {
      formData.append('file__group__clgr_icon', group.logo);
    }
  }
  const addressSection = {
    address_type: address.addressTypeId,
    email: address.email,
    ...('addressId' in address
      ? { address: address.addressId }
      : {
          country: address.countryId,
          line1: address.line1,
          line2: address.line2,
          line3: address.line3,
          postcode: address.postcode,
          city: address.city,
        }),
  };
  const sections = {
    group: groupSection,
    general: {
      clie_name: client.name,
      clie_legalname: client.legalName,
      clie_business_segment: client.businessSegment,
      clie_sect: client.sectorId,
      clie_clrs: client.relationshipStatusId,
      clie_manager: client.managerId,
      clie_coordinator: client.coordinatorId,
      clie_company_registry_number: client.registryNumber,
      clie_vat_number: client.vatNumber,
      clie_estimated_revenue_potential: client.revenuePotential,
      clie_website: client.website,
      clie_phone: client.phone,
      clie_notes: client.notes,
    },
    address: addressSection,
    contacts: payload.contacts.map(contactToDto),
  };
  formData.append('payload', JSON.stringify({ sections }));
  return formData;
}
